import { useEffect, useState } from 'react';

import type { ThemeOption } from './components/theme-selector';

const THEME_CLASSNAMES = [
  'light-theme',
  'dark-theme',
  'midnight-theme',
  'purple-dark-theme',
  'bubblegum-theme',
  'ocean-theme',
  'forest-theme',
  'aurora-theme',
  'ember-theme',
  'citrus-theme',
  'sandstone-theme',
  'slate-theme',
  'glacier-theme',
  'cyberpunk-theme',
  'rainbow-theme',
  'retro-theme',
  'noir-theme'
] as const;

const THEME_CLASS_MAP: Record<string, string | null> = {
  auto: null,
  light: 'light-theme',
  dark: 'dark-theme',
  midnight: 'midnight-theme',
  'purple-dark': 'purple-dark-theme',
  bubblegum: 'bubblegum-theme',
  ocean: 'ocean-theme',
  forest: 'forest-theme',
  aurora: 'aurora-theme',
  ember: 'ember-theme',
  citrus: 'citrus-theme',
  sandstone: 'sandstone-theme',
  slate: 'slate-theme',
  glacier: 'glacier-theme',
  cyberpunk: 'cyberpunk-theme',
  rainbow: 'rainbow-theme',
  retro: 'retro-theme',
  noir: 'noir-theme'
};

const DARK_THEMES = new Set<ThemeOption>([
  'dark',
  'midnight',
  'purple-dark',
  'aurora',
  'ember',
  'slate',
  'cyberpunk',
  'noir'
]);

export type SemanticThemeColorToken =
  | 'foreground'
  | 'muted-foreground'
  | 'card-foreground'
  | 'primary'
  | 'primary-foreground'
  | 'secondary'
  | 'secondary-foreground'
  | 'accent'
  | 'accent-foreground'
  | 'destructive'
  | 'destructive-foreground'
  | 'map-label'
  | 'map-border'
  | 'map-background'
  | 'map-road'
  | 'map-water'
  | 'map-landuse';

export type SemanticThemeColorPalette = Partial<Record<SemanticThemeColorToken, string>>;

const SEMANTIC_THEME_TOKEN_VARS: Record<SemanticThemeColorToken, string> = {
  foreground: '--foreground',
  'muted-foreground': '--muted-foreground',
  'card-foreground': '--card-foreground',
  primary: '--primary',
  'primary-foreground': '--primary-foreground',
  secondary: '--secondary',
  'secondary-foreground': '--secondary-foreground',
  accent: '--accent',
  'accent-foreground': '--accent-foreground',
  destructive: '--destructive',
  'destructive-foreground': '--destructive-foreground',
  'map-label': '--map-label',
  'map-border': '--map-border',
  'map-background': '--map-background',
  'map-road': '--map-road',
  'map-water': '--map-water',
  'map-landuse': '--map-landuse',
};

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(theme: ThemeOption, root: HTMLElement = document.documentElement): void {
  if (typeof document === 'undefined') {
    return;
  }

  let className: string | null;
  let dataThemeValue: string;
  let darkMode: boolean;

  if (theme === 'auto') {
    const prefersDark = getSystemPrefersDark();
    className = prefersDark ? 'dark-theme' : 'light-theme';
    dataThemeValue = 'auto';
    darkMode = prefersDark;
  } else {
    className = THEME_CLASS_MAP[theme] || null;
    darkMode = DARK_THEMES.has(theme);
    dataThemeValue = theme;
  }

  const body = document.body;

  // Clear previous theme classes
  root.classList.remove(...THEME_CLASSNAMES);
  if (body) {
    body.classList.remove(...THEME_CLASSNAMES);
  }

  // Apply new theme classes
  if (className) {
    root.classList.add(className);
    if (body) {
      body.classList.add(className);
    }
  }

  // Toggle dark mode helpers and data-theme attributes
  root.classList.toggle('dark', darkMode);
  root.setAttribute('data-theme', dataThemeValue);
  root.style.colorScheme = darkMode ? 'dark' : 'light';

  if (body) {
    body.classList.toggle('dark', darkMode);
    body.setAttribute('data-theme', dataThemeValue);
    body.style.colorScheme = darkMode ? 'dark' : 'light';
  }
}

export function resolveThemeSemanticColorPalette(
  theme: Exclude<ThemeOption, 'auto'>,
): SemanticThemeColorPalette {
  if (typeof document === 'undefined') {
    return {};
  }

  const probe = document.createElement('div');
  const className = THEME_CLASS_MAP[theme];
  if (className) {
    probe.classList.add(className);
  }
  probe.setAttribute('data-theme', theme);
  probe.style.position = 'absolute';
  probe.style.pointerEvents = 'none';
  probe.style.opacity = '0';
  probe.style.width = '0';
  probe.style.height = '0';
  document.body.appendChild(probe);

  try {
    const computed = getComputedStyle(probe);
    return (Object.keys(SEMANTIC_THEME_TOKEN_VARS) as SemanticThemeColorToken[]).reduce<SemanticThemeColorPalette>(
      (palette, token) => {
        const value = computed.getPropertyValue(SEMANTIC_THEME_TOKEN_VARS[token]).trim();
        if (value) {
          palette[token] = value;
        }
        return palette;
      },
      {},
    );
  } finally {
    probe.remove();
  }
}

const semanticPalettesEqual = (
  left: SemanticThemeColorPalette,
  right: SemanticThemeColorPalette,
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  return leftKeys.every(key => left[key as SemanticThemeColorToken] === right[key as SemanticThemeColorToken]);
};

export function useResolvedThemeSemanticColorPalette(
  theme: Exclude<ThemeOption, 'auto'>,
): SemanticThemeColorPalette {
  const [palette, setPalette] = useState<SemanticThemeColorPalette>(() => resolveThemeSemanticColorPalette(theme));

  useEffect(() => {
    if (typeof window === 'undefined') {
      setPalette(resolveThemeSemanticColorPalette(theme));
      return;
    }

    let cancelled = false;
    let timeoutId: number | null = null;
    let animationFrameId: number | null = null;

    const refreshPalette = () => {
      if (cancelled) {
        return;
      }
      const nextPalette = resolveThemeSemanticColorPalette(theme);
      setPalette(currentPalette => (semanticPalettesEqual(currentPalette, nextPalette) ? currentPalette : nextPalette));
    };

    refreshPalette();
    animationFrameId = window.requestAnimationFrame(() => {
      refreshPalette();
      timeoutId = window.setTimeout(refreshPalette, 0);
    });
    window.addEventListener('load', refreshPalette);

    return () => {
      cancelled = true;
      window.removeEventListener('load', refreshPalette);
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [theme]);

  return palette;
}
